import { motion } from 'motion/react';
import { Link } from 'react-router-dom';
import { Home, ArrowLeft, Shield, Search, Trash, Eye } from 'lucide-react';

interface ErrorPageProps {
  code: 403 | 404 | 410 | 451 | 500;
}

export default function ErrorPage({ code }: ErrorPageProps) {
  const errorConfig = {
    403: {
      title: 'Access Forbidden',
      description: 'You don\'t have permission to access this resource. If you believe this is an error, please contact the administrator.',
      icon: Shield,
      color: 'text-red-500',
      bgColor: 'bg-red-50'
    },
    404: {
      title: 'Page Not Found',
      description: 'The page you\'re looking for doesn\'t exist. It might have been moved, deleted, or you entered the wrong URL.',
      icon: Search,
      color: 'text-blue-500',
      bgColor: 'bg-blue-50'
    },
    410: {
      title: 'Gone',
      description: 'This page once existed but has been permanently removed. The content is no longer available.',
      icon: Trash,
      color: 'text-gray-500',
      bgColor: 'bg-gray-50'
    },
    451: {
      title: 'Unavailable For Legal Reasons',
      description: 'This content is not available due to legal restrictions in your region.',
      icon: Eye,
      color: 'text-yellow-500',
      bgColor: 'bg-yellow-50'
    },
    500: {
      title: 'Internal Server Error',
      description: 'Something went wrong on our end. We\'re working to fix this issue. Please try again later.',
      icon: Shield,
      color: 'text-purple-500',
      bgColor: 'bg-purple-50'
    }
  };

  const config = errorConfig[code];

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="min-h-screen flex items-center justify-center px-4"
    >
      <div className="max-w-2xl w-full text-center">
        <motion.div
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 0.6, ease: 'easeOut' }}
          className="mb-8"
        >
          {/* Error Code */}
          <div className="text-8xl md:text-9xl font-light text-muted-foreground/30 mb-4">
            {code}
          </div>
          
          {/* Icon */}
          <div className={`w-16 h-16 mx-auto mb-6 ${config.bgColor} rounded-2xl flex items-center justify-center`}>
            <config.icon className={`w-8 h-8 ${config.color}`} />
          </div>
          
          {/* Title */}
          <h1 className="text-3xl md:text-4xl text-foreground mb-4">
            {config.title}
          </h1>
          
          {/* Description */}
          <p className="text-muted-foreground text-lg leading-relaxed mb-8 max-w-lg mx-auto">
            {config.description}
          </p>
        </motion.div>

        {/* Actions */}
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-4"
        >
          <Link
            to="/"
            className="inline-flex items-center space-x-2 bg-foreground text-background px-8 py-4 rounded-xl hover:bg-foreground/90 transition-colors group"
          >
            <Home className="w-4 h-4" />
            <span>Go Home</span>
          </Link>
          
          <button
            onClick={() => window.history.back()}
            className="inline-flex items-center space-x-2 border border-border px-8 py-4 rounded-xl hover:bg-accent transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Go Back</span>
          </button>
        </motion.div>

        {/* Additional Help */}
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="mt-12"
        >
          <p className="text-muted-foreground text-sm mb-4">
            Still having trouble?
          </p>
          
          <div className="flex flex-col sm:flex-row items-center justify-center space-y-2 sm:space-y-0 sm:space-x-4 text-sm">
            <Link 
              to="/case-studies" 
              className="text-purple-600 hover:text-purple-700 transition-colors"
            >
              Browse Case Studies
            </Link>
            <span className="hidden sm:block text-muted-foreground">•</span>
            <Link 
              to="/about" 
              className="text-purple-600 hover:text-purple-700 transition-colors"
            >
              About Me
            </Link>
            <span className="hidden sm:block text-muted-foreground">•</span>
            <Link 
              to="/contact" 
              className="text-purple-600 hover:text-purple-700 transition-colors"
            >
              Contact
            </Link>
          </div>
        </motion.div>

        {/* Decorative Elements */}
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 -z-10">
          <motion.div
            animate={{ 
              rotate: 360,
              scale: [1, 1.1, 1]
            }}
            transition={{ 
              rotate: { duration: 20, repeat: Infinity, ease: 'linear' },
              scale: { duration: 4, repeat: Infinity, ease: 'easeInOut' }
            }}
            className="w-96 h-96 border border-purple-100 rounded-full opacity-20"
          />
        </div>
        
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 -z-10">
          <motion.div
            animate={{ 
              rotate: -360,
              scale: [1.1, 1, 1.1]
            }}
            transition={{ 
              rotate: { duration: 25, repeat: Infinity, ease: 'linear' },
              scale: { duration: 6, repeat: Infinity, ease: 'easeInOut' }
            }}
            className="w-64 h-64 border border-blue-100 rounded-full opacity-20"
          />
        </div>
      </div>
    </motion.div>
  );
}
